/* eslint-disable react/jsx-filename-extension */
import React, {
  forwardRef, useEffect, useImperativeHandle, useRef,
} from 'react';

const CANVAS = Object.freeze({
  HEIGHT: 960,
  WIDTH: 1280,
});

const LINE_HEIGHT_RATIO = 1.2;

const REDUCINGFACTOR = 4;

const reduceValue = (value) => (
  value / REDUCINGFACTOR
);

const Canvas = forwardRef(({
  lang,
  backgroundImage,
  backgroundCoverImage,
  texts,
}, ref) => {
  const canvasRef = useRef();

  useImperativeHandle(ref, () => ({
    getCanvasImage: async () => {
      const { ctx, canvas } = prepareCanvas();
      await renderCanvasImage(ctx, backgroundImage, backgroundCoverImage);

      // eslint-disable-next-line no-restricted-syntax
      for (const text of texts) {
        // eslint-disable-next-line no-await-in-loop
        await drawText(ctx, text);
      }
      return canvas.toDataURL('image/png');
    },
    getTextHeight: async (index) => {
      const { ctx } = prepareCanvas();
      const textHeight = await getTextHeight(ctx, texts[index]);
      return textHeight;
    },
  }));

  useEffect(() => {
    const { ctx } = prepareCanvas();
    renderCanvasImage(ctx, backgroundImage, backgroundCoverImage);
  }, [backgroundImage, backgroundCoverImage, texts]);

  const prepareCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();

    return { ctx, canvas };
  };

  async function renderCanvasImage(ctx) {
    return Promise.all([backgroundImage, backgroundCoverImage].map(imgLoad)).then((images) => {
      if (images[0]) ctx.drawImage(images[0], 0, 0, CANVAS.WIDTH, CANVAS.HEIGHT);
      if (images[1]) ctx.drawImage(images[1], 0, 0, CANVAS.WIDTH, CANVAS.HEIGHT);
    });
  }

  function imgLoad(base64) {
    return new Promise((resolve) => {
      if (!base64) {
        resolve();
      } else {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = base64;
        img.onload = () => {
          resolve(img);
        };
      }
    });
  }

  const wrapText = (context, text, maxWidth) => {
    const words = text.split(' ');
    let line = '';
    const lines = [];
    for (let n = 0; n < words.length; n += 1) {
      let testLine = `${line} ${words[n]}`;
      if (testLine[0] === ' ') {
        testLine = testLine.substring(1);
      }
      if (n === 0) {
        testLine = words[n];
      }
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth) {
        const { width: wordWidth } = context.measureText(words[n]);
        if (wordWidth > maxWidth) {
          const longWord = words[n];
          for (let cutIndex = longWord.length - 1; cutIndex >= 0; cutIndex -= 1) {
            const cuttedLongWord = longWord.substring(0, cutIndex);
            let lineWithCuttedLongWord = `${line} ${cuttedLongWord}`;
            if (lineWithCuttedLongWord[0] === ' ') {
              lineWithCuttedLongWord = lineWithCuttedLongWord.substring(1);
            }
            const { width: cuttedWidth } = context.measureText(lineWithCuttedLongWord);
            if (cuttedWidth <= maxWidth) {
              lines.push(lineWithCuttedLongWord);
              line = '';
              words.splice(n + 1, 0, longWord.substring(cutIndex));
              break;
            }
          }
        } else {
          lines.push(line);
          line = words[n];
        }
      } else {
        line = testLine;
      }
    }
    lines.push(line);
    return lines;
  };

  const getTextHeight = async (ctx, text) => {
    try {
      ctx.save();

      const {
        width,
        fontFamily,
        fontSize: baseFontSize,
      } = text;

      // generate lines

      const baseFontText = `${baseFontSize}px ${fontFamily}`;
      ctx.font = baseFontText;

      await document.fonts.load(baseFontText);

      const lines = [];

      const splittedLines = text[lang].split(/\r?\n/g);

      splittedLines.forEach((currentText) => {
        const newLines = wrapText(ctx, currentText, width - ((1 / 100) * width));
        newLines.forEach((line) => lines.push(line));
      });

      return lines.length * baseFontSize * LINE_HEIGHT_RATIO;
    } catch (e) {
      console.log('Error: getTextHeight');
      return 0;
    } finally {
      ctx.restore();
      console.log('getTextHeight obtained');
    }
  };

  const drawText = async (ctx, element) => {
    try {
      ctx.save();

      const {
        x,
        y,
        width,
        fontFamily,
        fontSize: baseFontSize,
        color,
        rotate,
      } = element;

      // generate lines

      const baseFontText = `${baseFontSize}px ${fontFamily}`;
      ctx.font = baseFontText;

      await document.fonts.load(baseFontText);

      const lines = [];

      const splittedLines = element[lang].split(/\r?\n/g);

      splittedLines.forEach((currentText) => {
        const newLines = wrapText(ctx, currentText, width - ((1 / 100) * width));
        newLines.forEach((line) => lines.push(line));
      });

      // set context properties

      const fontSize = baseFontSize;
      const fontText = `${fontSize}px ${fontFamily}`;

      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = fontText;

      // rotate around the center of the text square
      const cx = x + 0.5 * width;
      const cy = y + 0.5 * (lines.length * baseFontSize * LINE_HEIGHT_RATIO);

      ctx.translate(cx, cy);
      ctx.rotate(rotate * (Math.PI / 180));
      ctx.translate(-cx + x + width / 2, -cy + y);

      if (!document.fonts.check(fontText)) {
        await document.fonts.load(fontText);
      }

      let startFillY = fontSize * 0.4;
      lines.forEach((line) => {
        ctx.fillText(line, 0, startFillY);
        startFillY += fontSize * LINE_HEIGHT_RATIO;
      });
    } catch (e) {
      console.log('Error: drawText');
    } finally {
      ctx.restore();
      console.log('text drawn');
    }
  };

  return (
    <canvas
      ref={canvasRef}
      height={CANVAS.HEIGHT}
      width={CANVAS.WIDTH}
      style={styles.canvas}
    />
  );
});

const TemplateText = ({
  text,
  isTextRectShown,
  lang,
  onChangeText,
}) => {
  const textContainerRef = useRef();

  useEffect(() => {
    if (textContainerRef.current) {
      onChangeText(textContainerRef.current.clientHeight);
    }
  }, [lang, text[lang], text.width]);

  if (text[lang]
    || text[lang === 'en' ? 'defaultTextEn' : 'defaultTextFr']
  ) {
    return (
      <p
        ref={textContainerRef}
        style={{
          position: 'absolute',
          margin: 0,
          padding: 4,
          border: `${isTextRectShown ? 1 : 0}px solid black`,
          wordWrap: 'break-word',
          top: reduceValue(text.y),
          left: reduceValue(text.x),
          width: reduceValue(text.width) + (isTextRectShown ? 2 : 0),
          fontSize: reduceValue(text.fontSize),
          lineHeight: `${reduceValue(text.fontSize * LINE_HEIGHT_RATIO)}px`,
          fontFamily: text.fontFamily,
          textAlign: 'center',
          transform: `rotate(${text.rotate}deg)`,
          color: text.color,
          whiteSpace: 'pre-line',
        }}
      >
        {text[lang]
          || text[lang === 'en' ? 'defaultTextEn' : 'defaultTextFr']
          || ''}
      </p>
    );
  }
  return (<p />);
};

const TemplateCanvas = forwardRef(({
  backgroundImage,
  backgroundCoverImage,
  texts,
  onChangeText,
  lang,
  isTextRectShown,
}, ref) => {
  const canvasWrapperRef = React.useRef();

  useImperativeHandle(ref, () => ({
    async getCanvasImage() {
      return new Promise(async (resolve, reject) => {
        resolve(await canvasWrapperRef.current.getCanvasImage());
      });
    },
    async getTextHeight(index) {
      return new Promise(async (resolve, reject) => {
        resolve(await canvasWrapperRef.current.getTextHeight(index));
      });
    },
  }));

  return (
    <div style={styles.canvasContainer}>
      <Canvas
        lang={lang}
        backgroundImage={backgroundImage}
        backgroundCoverImage={backgroundCoverImage}
        texts={texts}
        ref={canvasWrapperRef}
      />
      <div style={styles.canvasOverlayContainer}>
        {texts.map((text, index) => (
          <TemplateText
            key={index}
            lang={lang}
            text={text}
            isTextRectShown={isTextRectShown}
            onChangeText={(value) => onChangeText(value, 'height', index)}
          />
        ))}
      </div>
    </div>
  );
});

const styles = {
  canvasContainer: {
    position: 'relative',
    width: reduceValue(CANVAS.WIDTH),
    height: reduceValue(CANVAS.HEIGHT),
    marginBottom: 20,
    overflow: 'hidden',
  },
  canvasOverlayContainer: {
    position: 'relative',
    top: 0,
    left: 0,
    margin: 0,
    borderWidth: 1,
  },
  canvas: {
    height: reduceValue(CANVAS.HEIGHT),
    width: reduceValue(CANVAS.WIDTH),
    position: 'absolute',
    top: 0,
    left: 0,
    margin: 0,
  },
};

export default TemplateCanvas;



// WEBPACK FOOTER //
// ./src/components/templates/templateCanvas.js